import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { RadioButton, Table } from "@wfp/ui";
import { DataTableProps } from "./data-table.types";

/**
 * Generic data table component with role-based column filtering and sorting
 */
export function DataTable<T>({
  columns,
  data,
  userRole,
  sortInfo,
  onSort,
  onRowClick,
  onRowSelect,
  getRowKey,
}: DataTableProps<T>) {
  const { t } = useTranslation();

  // Filter columns based on user role
  const visibleColumns = useMemo(() => {
    if (!userRole) return columns;
    return columns.filter(
      (col) => !col.roles || col.roles.length === 0 || col.roles.includes(userRole)
    );
  }, [columns, userRole]);

  // Build sortable state for visible columns
  const sortables = useMemo(() => {
    if (!sortInfo) return [];
    return visibleColumns.map((col) => {
      const sort = sortInfo.find((s) => s.field === col.sortField);
      return (
        sort || {
          code: col.key,
          field: col.sortField || col.key,
          mode: 0,
          enabled: col.sortable || false,
        }
      );
    });
  }, [visibleColumns, sortInfo]);

  const handleSort = (idx: number) => {
    if (!onSort) return;
    const sortable = sortables[idx];
    if (!sortable || !sortable.enabled) return;

    const newMode = (sortable.mode + 1) % 3;
    const value =
      newMode === 1
        ? `+${sortable.field}`
        : newMode === 2
        ? `-${sortable.field}`
        : sortable.field;
    onSort(value);
  };

  return (
    <Table>
      <thead>
        <tr className="item-row-head">
          {onRowSelect && <th></th>}
          {visibleColumns.map((col, idx) => (
            <th
              key={col.key}
              onClick={() => handleSort(idx)}
              className={onSort && sortables[idx]?.enabled ? "cursor-pointer" : ""}
            >
              {t(col.headerKey)}
              {sortables[idx] && (
                <span className="ml-0.5">
                  {sortables[idx].mode === 2
                    ? "\u25bc"
                    : sortables[idx].mode === 0
                    ? "\u2000"
                    : "\u25b2"}
                </span>
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map((item) => (
          <tr
            key={getRowKey(item)}
            className="item-row cursor-pointer"
            onClick={() => onRowClick?.(item)}
          >
            {onRowSelect && (
              <td onClick={(e) => e.stopPropagation()}>
                <RadioButton
                  labelText={""}
                  name="row-select"
                  value={getRowKey(item).toString()}
                  onClick={() => onRowSelect(item)}
                />
              </td>
            )}
            {visibleColumns.map((col) => (
              <td key={col.key} className="item-col text-start">
                {col.render(item, userRole)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
